import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactPlaceholder from 'react-placeholder';

import {
	makeStyles,
	useTheme,
	useMediaQuery,
	Grid,
	Divider
} from '@material-ui/core';

import Constants from "../utils/Constants";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import PaperActivity from "./PaperActivity";
import ActivityWorkflowActions from "./ActivityWorkflowActions";
import AssociationsPanel from "./AssociationsPanel";
import ActivityActions from "./ActivityActions";
import UserActivityTrail from "./UserActivityTrail";
//import RateAlertButton from "./RateAlertButton";
import RateAlertDialog from "./RateAlertDialog";
import CareAlertDialog from "./CareAlertDialog";
import AuthorizedUser from '../utils/AuthorizedUser';
import Heading from "./Heading";
import Fido from '../utils/Fido';
import KeywordsMatchesPanel from './KeywordsMatchesPanel';
import PaddedContent from './PaddedContent';

const styles = makeStyles(theme => ({
	gridItem: {
		padding: theme.spacing(3),

		[theme.breakpoints.down('xs')]: {
			paddingLeft: theme.spacing(1),
			paddingRight: theme.spacing(1)
		}
	},

	actionsItem: {
		paddingLeft: 0,

		[theme.breakpoints.down('sm')]: {
			paddingLeft: theme.spacing(3)
		}
	},

	paddedContent: {
		// backgroundColor: '#e9e9e9',
		backgroundColor: theme.palette.primary.grey,
		[theme.breakpoints.down('xs')]: {
			padding: theme.spacing(0.5)
		}
	},

	sectionDivider: {
		marginTop: theme.spacing(2.5),
		marginBottom: theme.spacing(2.5)
	},

	underActionBtnsDivider: {
		marginTop: theme.spacing(4.25)
	},

	userActivity: {
		marginTop: theme.spacing(4)
	}
}));

function IndividualActivity(props) {
	const [rateDlgOpen, setRateDlgOpen] = useState(false);
	const [careDlgOpen, setCareDlgOpen] = useState(false);
	const [nextStepStatus, setNextStepStatus] = useState(null);
	const authenticatedUser = useContext(AuthorizedUserContext);
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

	let classes = styles(),
		{ activity, isDeleted, history,
			saveHandler, shareHandler, noActionHandler,
			addCommentHandler, translateHandler, rateHandler, moveStatusHandler, careStatusHandler, suppressRateHandler, searchTerm, showSnackbar } = props,
		{ activityId, category, associations, flagged, auditTrail, sourceName, status, careStatus,
			auditTrailShared, auditTrailSaved, auditTrailRemoved, auditTrailRated, auditTrailViewed } = activity || {};

	const isRateInWorkflow = () => {
		return !authenticatedUser.noRateOnDelete && category === Constants.ACTIVITY.CATEGORIES.ALERTS.ID;
	};

	const isCareStatusActive = () => {
		return authenticatedUser && AuthorizedUser.hasCare(authenticatedUser) && careStatus === Constants.ACTIVITY.CARE_STATUSES.SHARED.ID;
	}

	const moveStatus = (activityId, newStatus, msg) => {
		if (newStatus === Constants.ACTIVITY.STATUSES.RESOLVED.ID && isRateInWorkflow()) {
			setNextStepStatus({
				id: activityId,
				newStatus: newStatus,
				msg: msg
			});
			setRateDlgOpen(true);
		} else {
			props.moveStatusHandler(activityId, newStatus, msg);
		}
	};

	const changeCareStatus = (event, activityId, currentStatus) => {
		let newStatus = (currentStatus ? 0 : 1)
		careStatusHandler(activityId, newStatus, '')
	}

	const noAction = () => {
		// update the esclated counter 
		const numberCircleElement = document.querySelector('#eslcatedCount');
		if (numberCircleElement) {
			const fido = new Fido();
			fido.fetch('/esclatedAlerts/esclatedCount', {
				method: "GET"
			})
				.then((data) => {
					const newCount = data.count ? data.count : 0;
					numberCircleElement.innerHTML = newCount;
					// Logic that depends on the updated count
					if (nextStepStatus === null && isRateInWorkflow()) {
						setNextStepStatus(true);
						setRateDlgOpen(true);
					} else {
						props.noActionHandler();
					}
				})
				.catch(error => {
					console.error(error);
				});
		}
	};

	const removeAction = () => {
		if (isCareStatusActive()) {
			setCareDlgOpen(true);
		} else {
			props.noActionHandler();
		}
	}

	const gotoNextStepCare = () => {
		props.noActionHandler();
	}

	const gotoNextStep = () => {
		closeRateDlg();

		if (nextStepStatus === true) {
			props.noActionHandler();
		} else {
			moveStatusHandler(nextStepStatus.id, nextStepStatus.newStatus, nextStepStatus.msg);
		}

		setNextStepStatus(null);
	};

	const closeCareDlg = () => {
		setCareDlgOpen(false);
	}

	const closeRateDlg = () => {
		setRateDlgOpen(false);
		setNextStepStatus(null);
	};

	let actionBtns = Constants.ACTIVITY.ACTION_BUTTONS,
		showAssociations = associations && associations.length,
		showMatches = activity.postMatches && activity.postMatches.length,

		activityWorkflowActions = (
			<ActivityWorkflowActions
				status={status}
				careStatus={careStatus}
				sourceName={sourceName}
				isDeleted={isDeleted}
				activityId={activityId}
				noActionHandler={noAction}
				careStatusHandler={changeCareStatus}
				showSnackbar={showSnackbar}
				activity={activity}
				moveStatusHandler={moveStatus} />
		),

		activityActions,
		actionsArray = [
			actionBtns.getRemove({
				disabled: isDeleted,
				handleClick: removeAction
			}),
			actionBtns.getSave({
				flagged: flagged,
				disabled: isDeleted,
				handleClick: saveHandler
			}),
			actionBtns.getShare({
				disabled: isDeleted,
				handleClick: shareHandler
			})
		];

	let myRating, rateDialog, careDialog;
	if (authenticatedUser) {
		if (auditTrailRated) {
			myRating = auditTrailRated.find(rating => rating.user.id === authenticatedUser.id) || { alert_id: activityId };
		}

		rateDialog = (
			<RateAlertDialog
				rating={myRating}
				open={rateDlgOpen}
				rateHandler={rateHandler}
				closeHandler={closeRateDlg}
				nextStepHandler={nextStepStatus ? gotoNextStep : null}
				suppressRate={authenticatedUser.noRateOnDelete}
				suppressRateHandler={suppressRateHandler}
				hasCareFeature={isCareStatusActive()}
				activityId={activityId}
				careStatusHandler={careStatusHandler}
				hasSuppressFeature={authenticatedUser.accountFeatures.includes(Constants.ACCOUNT.FEATURES.DISABLE_RATE_OPTION)} />
		);
	}

	if (authenticatedUser && AuthorizedUser.hasCare(authenticatedUser)) {
		careDialog = <CareAlertDialog
			open={careDlgOpen}
			closeHandler={closeCareDlg}
			activityId={activityId}
			careStatusHandler={careStatusHandler}
			nextStepHandler={gotoNextStepCare}
		/>
	}

	if (category === Constants.ACTIVITY.CATEGORIES.ALERTS.ID) {
		actionsArray.push(
			actionBtns.getRate({
				disabled: isDeleted,
				rated: myRating && myRating.id != null,
				handleClick: () => setRateDlgOpen(true)
			})
		);
	}

	activityActions = (
		<ReactPlaceholder type="rect" ready={activityId != null} style={{ width: "100%", height: 80 }}>
			<ActivityActions actions={actionsArray} />
		</ReactPlaceholder>
	);

	const getUniqueMatchDetails = (matches) => {
		const uniqueMatchDetails = [];

		matches.forEach((match) => {
			if (!uniqueMatchDetails.includes(match.details)) {
				uniqueMatchDetails.push(match.details);
			}
		});

		return uniqueMatchDetails;
	}

	const uniqueMatchDetails = getUniqueMatchDetails(activity.postMatches || []);

	return (
		<React.Fragment>
			{rateDialog}
			{careDialog}
			<Grid container>
				<Grid
					item
					className={classes.gridItem}
					xs={12} sm={12} md={8} lg={8} xl={8}>

					{isSmall && authenticatedUser ? activityWorkflowActions : null}

					<PaperActivity
						{...props}
						{...activity}
						numberOfViewableComments={3}
						handleTranslateClick={translateHandler}
						searchTerm={searchTerm}
						activityActions={isSmall && authenticatedUser ? activityActions : null}
						addCommentHandler={authenticatedUser ? addCommentHandler : null} />

					{authenticatedUser ?
						<UserActivityTrail
							category={activity.category}
							shared={auditTrailShared}
							saved={auditTrailSaved}
							removed={auditTrailRemoved}
							rated={auditTrailRated}
							viewed={auditTrailViewed}
							auditTrail={auditTrail}
							className={classes.userActivity} /> : null
					}
				</Grid>
				<Grid
					item
					className={`${classes.gridItem} ${classes.actionsItem}`}
					xs={12} sm={12} md={4} lg={4} xl={4}>

					{!isSmall && authenticatedUser ?
						<React.Fragment>
							<PaddedContent className={classes.paddedContent}>
								{activityWorkflowActions}
								<Divider className={classes.sectionDivider} />
								{activityActions}
							</PaddedContent>
						</React.Fragment> : null
					}

					{showMatches || showAssociations ?
						<Divider className={`${classes.sectionDivider} ${classes.underActionBtnsDivider}`} />
						: null}

					{showMatches ?
						<React.Fragment>
							<KeywordsMatchesPanel matches={uniqueMatchDetails} />
						</React.Fragment> : null
					}

					{showAssociations ?
						<React.Fragment>
							<AssociationsPanel
								history={history}
								associations={associations} />
						</React.Fragment> : null
					}
				</Grid>
			</Grid>
		</React.Fragment>
	);
}

IndividualActivity.propTypes = {
	activity: PropTypes.object,
	isDeleted: PropTypes.bool,
	//location: PropTypes.object.isRequired,
	history: PropTypes.object.isRequired,
	saveHandler: PropTypes.func,
	shareHandler: PropTypes.func,
	moveStatusHandler: PropTypes.func,
	careStatusHandler: PropTypes.func,
	noActionHandler: PropTypes.func,
	addCommentHandler: PropTypes.func,
	translateHandler: PropTypes.func,
	rateHandler: PropTypes.func,
	auditTrail: PropTypes.array,
	auditTrailShared: PropTypes.array,
	auditTrailSaved: PropTypes.array,
	auditTrailRemoved: PropTypes.array,
	auditTrailRated: PropTypes.array,
	auditTrailViewed: PropTypes.array,
	suppressRateHandler: PropTypes.func,
	showSnackbar: PropTypes.func,
	//associations: PropTypes.array,
	//isActivityDeleted: PropTypes.bool,
	//handleRestoreAndSave: PropTypes.func,
};

export default IndividualActivity;